/**
 * Simple request deduplication to prevent concurrent duplicate API calls
 * Enhanced to properly handle FormData objects for unique key generation
 */

// In-flight request tracking for deduplication only
const activeRequests = new Map();

/**
 * Generate key from request config for deduplication tracking
 */
function getRequestKey(config) {
  const { method, url, params, data } = config;
  const key = `${method}:${url}`;

  if (params) {
    const paramString = new URLSearchParams(params).toString();
    return `${key}?${paramString}`;
  }

  if (data && method !== 'get') {
    // Handle FormData objects specially
    if (data instanceof FormData) {
      return getFormDataKey(key, data);
    }
    return `${key}:${JSON.stringify(data)}`;
  }

  return key;
}

/**
 * Generate unique key for FormData requests
 */
function getFormDataKey(baseKey, formData) {
  const fieldInfo = [];

  // Extract all form fields and their characteristics
  for (const [fieldName, value] of formData.entries()) {
    if (value instanceof File) {
      // For files, include name, size, and type for uniqueness
      fieldInfo.push(`${fieldName}:file:${value.name}:${value.size}:${value.type}`);
    } else {
      // For regular fields, include name and value
      fieldInfo.push(`${fieldName}:${value}`);
    }
  }

  // Sort to ensure consistent ordering
  fieldInfo.sort();

  // Create a hash-like identifier from the field info
  const fieldsIdentifier = fieldInfo.join('|');

  return `${baseKey}:formdata:${fieldsIdentifier}`;
}

/**
 * Check if URL is an S3 URL that should not be deduplicated
 */
function isS3Request(config) {
  const url = config.url || '';
  return /\.s3[.-]/.test(url) || url.includes('amazonaws.com');
}

/**
 * Simple request deduplication - prevents concurrent duplicate calls only
 */
export function deduplicateRequest(requestFn, config) {
  // Skip deduplication for S3 requests as they use unique presigned URLs
  if (isS3Request(config)) {
    return requestFn(config);
  }
  
  const requestKey = getRequestKey(config);
  
  // Check if identical request is already in flight
  if (activeRequests.has(requestKey)) {
    return activeRequests.get(requestKey);
  }
  
  // Execute request
  const requestPromise = requestFn(config)
    .finally(() => {
      // Remove from active requests when complete
      activeRequests.delete(requestKey);
    });
  
  // Track active request
  activeRequests.set(requestKey, requestPromise);
  
  return requestPromise;
}

/**
 * Get stats for debugging (development only)
 */
export function getStats() {
  return {
    activeRequests: activeRequests.size,
    activeRequestKeys: Array.from(activeRequests.keys())
  };
}